'use client';

import React, { useEffect } from 'react';

import { CloseDealerships } from '@/app/_components/close-dealerships/close-dealerships';
import { useDealersInfo, useGeoLocation } from '@/hooks/dealers/dealers.hook';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import { AfterSalesPagesData } from '@/services/after_sales/after-sales.service';
import { getFirstDealers } from '@/services/dealers/dealers.service';
import {
  getCurrentLocation,
  sortByDistance,
} from '@/services/location.service';
import { Range } from '@/types/request/request.types';
import { Special } from '@/types/specials/specials.types';

import { AboutAfterSales } from './after-sales/about/AfterSales';
import { HeroBanner } from './after-sales/hero-banner/HeroBanner';
import AfterSalesPromotions from './AfterSalesPromotions';
import CTA from './CTA';
import FinanceYourKia from './FinanceYourKia';
import WarrantySection from './WarrantySection';

type PageProps = {
  pageData: AfterSalesPagesData;
  dealers: any;
  specials: Special[];
  ranges: Range[];
  isCtaHidden: boolean;
};

const AfterSalesPage = ({
  pageData,
  dealers,
  specials,
  ranges,
  isCtaHidden,
}: PageProps) => {
  const { userLocation, setUserLocation } = useGeoLocation();
  const { listOfDealersByLocation, setListOfDealersByLocation } =
    useDealersInfo();
  const { openLeadForm } = useFloatingCTA();

  useEffect(() => {
    if (isCtaHidden) {
      openLeadForm('parts');
    }

    getCurrentLocation().then(({ lat, lng }) => {
      setUserLocation({ lat, lng });

      if (!lat && !listOfDealersByLocation.length) {
        setListOfDealersByLocation(getFirstDealers(dealers));
      }
    });
  }, [isCtaHidden]);

  useEffect(() => {
    const sortedDealerships = sortByDistance(userLocation, dealers);

    setListOfDealersByLocation(getFirstDealers(sortedDealerships));
  }, [userLocation, dealers, setListOfDealersByLocation]);

  return (
    <>
      <HeroBanner
        title={pageData?.title}
        description={pageData?.subheading}
        image={`/${pageData?.banner}`}
        banner_call_to_actions={pageData?.banner_call_to_actions}
        isCtaHidden={isCtaHidden}
      />
      <AboutAfterSales blocks={pageData?.blocks} />
      {specials?.length > 0 && (
        <AfterSalesPromotions
          specials={specials}
          slug={pageData?.slug}
          ranges={ranges}
        />
      )}
      {pageData?.warranties[0] ? (
        <WarrantySection warranty={pageData?.warranties[0]} />
      ) : null}
      <CTA call_to_actions={pageData?.call_to_actions} />
      <FinanceYourKia footer_call_to_action={pageData?.footer_call_to_action} />
      <CloseDealerships dealers={listOfDealersByLocation} />
    </>
  );
};

export default AfterSalesPage;
